import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { salesAPI, invoicesAPI } from '../../services/garageApi';
import { ArrowLeft, ShoppingCart, FileText, AlertCircle } from 'lucide-react';

const CreateSale = () => {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        invoice_id: '',
        sale_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        discount_percent: 0,
    });

    useEffect(() => {
        fetchPendingInvoices();
    }, []);

    const fetchPendingInvoices = async () => {
        try {
            const response = await invoicesAPI.getAll({ status: 'pending' });
            const data = Array.isArray(response.data) ? response.data : [];
            setInvoices(data);
        } catch (err) {
            console.error('Error fetching invoices:', err);
            setError('Failed to load invoices.');
        } finally {
            setLoading(false);
        }
    };

    const handleInvoiceSelect = async (invoiceId) => {
        setFormData(prev => ({ ...prev, invoice_id: invoiceId }));
        setSelectedInvoice(null);
        setError('');

        if (!invoiceId) return;

        try {
            const response = await invoicesAPI.show(invoiceId);
            setSelectedInvoice(response.data);
        } catch (err) {
            console.error('Error fetching invoice details:', err);
            setError('Failed to load invoice details.');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.invoice_id) {
            setError('Please select an invoice.');
            return;
        }

        try {
            setSubmitting(true);
            setError('');
            await salesAPI.create({
                invoice_id: formData.invoice_id,
                sale_date: formData.sale_date,
                payment_method: formData.payment_method,
                discount_percent: formData.discount_percent || 0,
            });
            navigate('/sales');
        } catch (err) {
            console.error('Error creating sale:', err);
            const message = err.response?.data?.error || err.response?.data?.message || 'Error creating sale. Please try again.';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="bg-white shadow rounded-lg">
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => navigate('/sales')}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            <div className="flex items-center space-x-2">
                                <ShoppingCart className="h-5 w-5 text-blue-600" />
                                <h5 className="text-lg font-semibold">Create Sale from Invoice</h5>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mx-6 mt-4 flex items-center space-x-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Invoice Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <FileText className="inline h-4 w-4 mr-1" />
                            Select Pending Invoice
                        </label>
                        <select
                            name="invoice_id"
                            value={formData.invoice_id}
                            onChange={(e) => handleInvoiceSelect(e.target.value)}
                            className="form-select w-full rounded-md border-gray-300"
                            required
                        >
                            <option value="">-- Select an Invoice --</option>
                            {invoices.map(invoice => (
                                <option key={invoice.id} value={invoice.id}>
                                    {invoice.id} — {invoice.customer?.name || 'Unknown'} — ${invoice.total_amount || invoice.net_amount || 0}
                                </option>
                            ))}
                        </select>
                        {invoices.length === 0 && (
                            <p className="mt-2 text-sm text-gray-500">No pending invoices found. Create an invoice first.</p>
                        )}
                    </div>

                    {/* Invoice Items Preview */}
                    {selectedInvoice && (
                        <div className="border rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-3 border-b">
                                <h6 className="text-sm font-medium text-gray-700">
                                    Invoice Items — {selectedInvoice.customer?.name}
                                </h6>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {(selectedInvoice.invoice_items || selectedInvoice.invoiceItems || []).map((item, index) => (
                                            <tr key={item.id || index}>
                                                <td className="px-4 py-2 text-sm text-gray-900">
                                                    {item.invoiceable?.name || item.invoiceable?.battery_name || item.invoiceable?.size || item.invoiceable?.service_name || `Item #${index + 1}`}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-500">
                                                    <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs capitalize">
                                                        {item.invoiceable_type || '-'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900 text-right">{item.quantity}</td>
                                                <td className="px-4 py-2 text-sm text-gray-900 text-right">${Number(item.unit_price).toFixed(2)}</td>
                                                <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                                                    ${(item.quantity * item.unit_price).toFixed(2)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-50">
                                        <tr>
                                            <td colSpan={4} className="px-4 py-2 text-sm font-medium text-gray-700 text-right">Invoice Total:</td>
                                            <td className="px-4 py-2 text-sm font-bold text-gray-900 text-right">
                                                ${Number(selectedInvoice.total_amount || selectedInvoice.net_amount || 0).toFixed(2)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Sale Details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sale Date</label>
                            <input
                                type="date"
                                name="sale_date"
                                value={formData.sale_date}
                                onChange={handleChange}
                                className="form-input w-full rounded-md border-gray-300"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                            <select
                                name="payment_method"
                                value={formData.payment_method}
                                onChange={handleChange}
                                className="form-select w-full rounded-md border-gray-300"
                                required
                            >
                                <option value="cash">Cash</option>
                                <option value="cheque">Cheque</option>
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                                Cash payments auto-create a receipt.
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Discount %</label>
                            <input
                                type="number"
                                name="discount_percent"
                                value={formData.discount_percent}
                                onChange={handleChange}
                                className="form-input w-full rounded-md border-gray-300"
                                min="0"
                                max="100"
                                step="0.01"
                            />
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-4 pt-6 border-t">
                        <button
                            type="button"
                            onClick={() => navigate('/sales')}
                            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || !formData.invoice_id}
                            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Creating Sale...' : 'Create Sale'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateSale;
